import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { LoadInquiryDetailsComplete } from '@tv3/store/inquiry/inquiry.actions'
import { isSomething, Rental } from '@tokeet-frontend/tv3-platform'
import { currencies } from '@tokeet-frontend/tv3-platform'
import { EVisitorResponse, EVisitorValidation, EVisitorCheckinPayload } from './evisitor.model'
import * as R from 'ramda'

@Injectable({
  providedIn: 'root',
})
export class EVisitorService {
  constructor(private http: HttpClient, private store: Store) {}

  /**
   * Send booking to eVisitor (check-in)
   * @param bookingId - Tokeet inquiry pkey
   * @param payload - EVisitor checkin payload
   * @param gatewayId - OAuth gateway id
   * @param guestPkey - Guest pkey for multi-guest bookings
   */
  checkIn(
    bookingId: string,
    payload: EVisitorCheckinPayload,
    gatewayId: string,
    guestPkey: string
  ): Observable<EVisitorResponse> {
    const payloadWithInquiry = {
      ...payload,
      inquiry_pkey: bookingId,
      guest_pkey: guestPkey,
    }

    return this.http
      .post<EVisitorResponse>(`@api/v1/providers/evisitor/checkins?gateway_id=${gatewayId}`, payloadWithInquiry)
      .pipe(switchMap((response) => this.loadInquiry(bookingId).pipe(map(() => response))))
  }

  /**
   * Fetch inquiry by ID and dispatch to store (same pattern as InquiryService.load)
   */
  private loadInquiry(bookingId: string): Observable<Inquiry> {
    const url = `@api/inquiry/${bookingId}?invoice_type=0,1&include_draft=1`

    return this.http.get<any>(url).pipe(
      map((inquiry) => {
        if (isSomething(inquiry.invoices)) {
          return {
            ...inquiry,
            invoices: inquiry.invoices.map((i: any) => ({
              ...i,
              currency: R.find((c: any) => c.code === i.currency?.code, currencies) || i.currency,
            })),
          }
        }
        return inquiry
      }),
      map(Inquiry.deserialize),
      tap((inquiry) => this.store.dispatch(LoadInquiryDetailsComplete({ inquiry })))
    )
  }

  /**
   * Deregister booking from eVisitor (check-out)
   * @param bookingId - Tokeet inquiry pkey
   * @param referenceId - The checked-in guest id (from inquiry.evisitor.checkins[].request.id)
   * @param gatewayId - OAuth gateway id
   * @param departure - Departure timestamp
   * @param guestPkey - Guest pkey for multi-guest bookings
   */
  checkout(
    bookingId: string,
    referenceId: string,
    gatewayId: string,
    departure: number,
    guestPkey: string
  ): Observable<EVisitorResponse> {
    const body = {
      inquiry_pkey: bookingId,
      departure,
      guest_pkey: guestPkey,
    }
    return this.http
      .post<EVisitorResponse>(
        `@api/v1/providers/evisitor/checkins/${referenceId}/checkout?gateway_id=${gatewayId}`,
        body
      )
      .pipe(switchMap((response) => this.loadInquiry(bookingId).pipe(map(() => response))))
  }

  /**
   * Cancel eVisitor registration
   * @param bookingId - Tokeet inquiry pkey
   * @param referenceId - The checked-in guest id (from inquiry.evisitor.checkins[].request.id)
   * @param gatewayId - OAuth gateway id
   * @param guestPkey - Guest pkey for multi-guest bookings
   */
  cancel(bookingId: string, referenceId: string, gatewayId: string, guestPkey: string): Observable<EVisitorResponse> {
    const body = {
      inquiry_pkey: bookingId,
      guest_pkey: guestPkey,
    }
    return this.http
      .post<EVisitorResponse>(`@api/v1/providers/evisitor/checkins/${referenceId}/cancel?gateway_id=${gatewayId}`, body)
      .pipe(switchMap((response) => this.loadInquiry(bookingId).pipe(map(() => response))))
  }

  /**
   * Validate booking data for eVisitor submission
   */
  validateBookingForEVisitor(inquiry: Inquiry, rental?: Rental): EVisitorValidation {
    const missingFields: string[] = []
    const errors: Record<string, string> = {}

    // Check guest legal name
    if (!inquiry?.guestDetails?.name && !inquiry?.guest?.name) {
      missingFields.push($localize`Guest legal name`)
    }

    // Check document type and number
    // Note: These fields may not exist in the current Guest model
    // They would need to be added as custom attributes or additional fields
    if (!(inquiry?.guest as any)?.documentType) {
      missingFields.push($localize`Document type`)
    }
    if (!(inquiry?.guest as any)?.documentNumber) {
      missingFields.push($localize`Document number`)
    }

    // Check citizenship
    if (!(inquiry?.guest as any)?.citizenship && !inquiry?.guest?.address?.country) {
      missingFields.push($localize`Citizenship`)
    }

    // Check dates
    if (!inquiry?.guestArrive) {
      missingFields.push($localize`Check-in date`)
      errors['guestArrive'] = $localize`Check-in date is required`
    }
    if (!inquiry?.guestDepart) {
      missingFields.push($localize`Check-out date`)
      errors['guestDepart'] = $localize`Check-out date is required`
    }

    // Check rental/unit mapping
    if (!inquiry?.rentalId) {
      missingFields.push($localize`Property/unit`)
      errors['rentalId'] = $localize`Booking must be linked to a property`
    }

    // Check date of birth (if required by provider)
    if (!inquiry?.guest?.birthday) {
      missingFields.push($localize`Date of birth`)
    }

    return {
      isValid: missingFields.length === 0,
      missingFields,
      errors,
    }
  }

  /**
   * Map provider error codes to user-friendly messages
   */
  mapErrorCodeToMessage(errorCode: string): string {
    const errorMap: Record<string, string> = {
      INVALID_DOCUMENT: $localize`The document number is invalid`,
      INVALID_DATE: $localize`The check-in or check-out date is invalid`,
      GUEST_ALREADY_REGISTERED: $localize`This guest is already registered`,
      SERVICE_UNAVAILABLE: $localize`eVisitor service is temporarily unavailable`,
      AUTHENTICATION_FAILED: $localize`Authentication with eVisitor failed`,
      INVALID_CITIZENSHIP: $localize`The citizenship information is invalid`,
      MISSING_REQUIRED_FIELD: $localize`Required field is missing`,
      CANCELLATION_NOT_ALLOWED: $localize`Cancellation is not allowed for this registration`,
      REFERENCE_NOT_FOUND: $localize`Registration reference not found`,
      TIMEOUT: $localize`Request to eVisitor timed out`,
    }

    return errorMap[errorCode] || $localize`An error occurred with eVisitor`
  }

  /**
   * Check if booking is eligible for eVisitor
   */
  isEVisitorEnabled(rental?: Rental | null): boolean {
    const evisitor = rental?.evisitor

    if (!evisitor) {
      return false
    }

    const hasFacility = !!evisitor.evisitor_facility_id?.toString().trim()
    const hasGateway = !!evisitor.evisitor_gateway_id?.toString().trim()

    return hasFacility && hasGateway
  }
}
